/**
 * usePendingCredits
 *
 * A user's submissions only become credits once an admin approves them. Until
 * then the contribution is invisible: nothing in the UI acknowledges that the
 * thing they sent in exists. This hook watches everything they've submitted
 * that is still awaiting review, and reports what it will be worth.
 *
 * It reads CONTRIBUTION_SOURCES — the same constant the approval/earning path
 * uses — so "pending" can never drift out of sync with what actually earns.
 * If a new earning collection is added there, it is tracked here for free.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { CONTRIBUTION_SOURCES } from '../constants/subscriptionPlans';
import { FUNDS_EARN_CENTS } from '../constants/userFunds';
import { isPendingCreditStatus, summarizePendingCredits } from '../utils/mapCoords';

export function usePendingCredits(uid) {
  const [byKey, setByKey] = useState({});
  const [loading, setLoading] = useState(Boolean(uid));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) {
      setByKey({});
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const db = getFirestore();
    const unsubs = [];

    CONTRIBUTION_SOURCES.forEach((source) => {
      // A source may record the submitter under more than one field name
      // (new_venues uses both userId and createdBy). Firestore can't OR across
      // fields in a single equality query, so subscribe per field and dedupe
      // by document id below.
      (source.userIdFields || ['userId']).forEach((field) => {
        const key = `${source.collection}:${field}`;
        const q = query(
          collection(db, source.collection),
          where(field, '==', uid),
        );

        unsubs.push(
          onSnapshot(
            q,
            (snap) => {
              const ids = [];
              snap.forEach((docSnap) => {
                if (isPendingCreditStatus(docSnap.data()?.status)) ids.push(docSnap.id);
              });
              setByKey((prev) => ({ ...prev, [key]: ids }));
              setLoading(false);
            },
            (err) => {
              console.warn('pending credits subscribe', source.collection, field, err);
              setError(err?.message || 'Could not load pending contributions');
              setLoading(false);
            },
          ),
        );
      });
    });

    return () => {
      unsubs.forEach((fn) => fn?.());
    };
  }, [uid]);

  const { pendingCount, pendingCents } = useMemo(
    () => summarizePendingCredits(byKey, FUNDS_EARN_CENTS),
    [byKey],
  );

  return { pendingCount, pendingCents, loading, error };
}

export default usePendingCredits;
